import {
  AutomaticQuotationPreferencesType,
  ConfigType,
  UpdateConfigType,
  UpdateWidgetSettingsType,
  WidgetSettingsType,
} from '@/constants/types/models/supplier/Config';
import Fetch, { getQueryParams, getToken } from './_utils';
import {
  SubsidiaryType,
  SubsidiaryWithSupplierConfigType,
} from '@/constants/types/models/supplier/Subsidiary';
import { ListAPIResponseType } from '@/constants/types/misc/api';
import {
  CreateSubsidiaryClientStatusType,
  DeleteSubsidiaryClientStatusType,
  ListSubsidiaryClientStatusType,
  SubsidiaryClientStatusType,
  UpdateSubsidiaryClientStatusType,
} from '@/constants/types/models/supplier/SubsidiaryClientStatus';
import {
  AssignmentGroupType,
  CreateAssignmentGroupType,
  DeleteAssignmentGroupType,
  ListAssignmentGroupType,
  UpdateAssignmentGroupType,
} from '@/constants/types/models/supplier/AssignmentGroup';
import {
  CreateDigitalMarketing,
  DigitalMarketingChannelsType,
  UpdateDigitalMarketing,
} from '@/constants/types/models/supplier/DigitalMarketingChannels';
import {
  CreateWorkScheduleType,
  WorkScheduleType,
} from '@/constants/types/models/supplier/WorkSchedules';
import {
  ListSubsidiaryUserType,
  SubsidiaryUserType,
} from '@/constants/types/models/supplier/SubsidiaryUser';
import {
  UpdateWhatsappReactivationConfig,
  WhatsappReactivationConfig,
} from '@/constants/types/models/supplier/WhatsappReactivationConfig';
import {
  UpdateWelcomeMessageConfig,
  WelcomeMessageConfig,
} from '@/constants/types/models/supplier/WelcomeMessageConfig';

export const getSubsidiaryConfigService = async (params: {
  signal?: AbortSignal;
}) => {
  const response = await Fetch('suppliers/config/', {
    method: 'GET',
    signal: params.signal,
  });
  if (response.ok) {
    const data = await response.json();
    return data as ConfigType;
  }
};

// Single source of truth for the subsidiary-config cache key — every reader
// must use it so any edit invalidates them all.
export const SUBSIDIARY_CONFIG_QUERY_KEY = ['subsidiaryConfig'] as const;

export const WIDGET_SETTINGS_QUERY_KEY = ['widgetSettings'] as const;

export const getWidgetSettingsService = async (params: {
  signal?: AbortSignal;
}) => {
  const response = await Fetch('suppliers/widget-settings/', {
    method: 'GET',
    signal: params.signal,
  });
  if (response.ok) {
    const data = await response.json();
    return data as WidgetSettingsType;
  }
  throw new Error('Error fetching widget settings');
};

export const updateWidgetSettingsService = async (
  payload: UpdateWidgetSettingsType
) => {
  const response = await Fetch('suppliers/widget-settings/', {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  if (response.ok) {
    const data = await response.json();
    return data as WidgetSettingsType;
  }
  throw new Error('Error updating widget settings');
};

export const updateSubsidiaryConfigService = async (
  config: UpdateConfigType
) => {
  // TODO: temporary patch — working_weekdays has allow_empty=False in the API but has no UI,
  // so the server value (empty []) must never be sent back. Proper fix: stop spreading the full
  // configData into form initialValues, or make the API tolerate the empty value.
  const { working_weekdays: _ww, ...safeConfig } = config;
  const response = await Fetch(`suppliers/config/update/`, {
    method: 'PATCH',
    body: JSON.stringify(safeConfig),
  });
  // Get response
  if (response.ok) {
    const data = await response.json();
    return data as ConfigType;
  }
  throw new Error('Error');
};

export const getSuppliersInfoService = async (): Promise<
  ConfigType | undefined
> => {
  const response = await Fetch('suppliers/config/', {
    method: 'GET',
  });

  if (response.ok) {
    const data = await response.json();

    return data;
  }
};

// Single source of truth for the subsidiary-detail cache key — matches the
// literal ['subsidiary'] used by the global queryFn default in
// TanstackQueryConfig.tsx and by other readers across the app.
export const SUBSIDIARY_QUERY_KEY = ['subsidiary'] as const;

/** Shared `queryFn` for SUBSIDIARY_QUERY_KEY. Registered as a prefix default in
 * TanstackQueryConfig, and passed explicitly by readers so they don't break
 * silently (a `useQuery` with no `queryFn` and no default just stays
 * `undefined` forever, with no error to notice). */
export const subsidiaryQueryFn = () => {
  const token = getToken();
  if (!token) return;
  return getSubsidiaryService(token['custom:subsidiary_id']);
};

export const getSubsidiaryService = async (subsidiaryId: string) => {
  const response = await Fetch(`suppliers/${subsidiaryId}/`, {
    method: 'GET',
  });

  if (response.ok) {
    const data = await response.json();

    return data as SubsidiaryWithSupplierConfigType;
  }
};

export const updateSubsidiaryService = async (
  params: Partial<Omit<SubsidiaryType, 'created_at' | 'updated_at'>>
) => {
  const response = await Fetch(`suppliers/${params.id}/update/`, {
    method: 'PATCH',
    body: JSON.stringify(params),
  });

  if (response.ok) {
    const data = await response.json();

    return data as SubsidiaryWithSupplierConfigType;
  }
};

export const getAutomaticQuotationPreferencesService = async () => {
  const response = await Fetch('suppliers/automatic-quotation-preferences/', {
    method: 'GET',
  });

  if (response.ok) {
    const data = await response.json();

    return data as ListAPIResponseType<AutomaticQuotationPreferencesType>;
  }
};

export const createAutomaticQuotationPreferenceService = async (
  params: AutomaticQuotationPreferencesType
) => {
  const response = await Fetch('suppliers/automatic-quotation-preferences/', {
    method: 'POST',
    body: JSON.stringify(params),
  });

  if (response.ok) {
    const data = await response.json();

    return data as AutomaticQuotationPreferencesType;
  }
};

export const getSubsidiaryClientStatusesService = async (
  params: ListSubsidiaryClientStatusType
) => {
  const queryParams = getQueryParams(params);

  const response = await Fetch(
    `suppliers/subsidiary_client_status/${queryParams}`,
    {
      method: 'GET',
      signal: params?.signal,
    }
  );

  if (response.ok) {
    const data = await response.json();
    return data as ListAPIResponseType<SubsidiaryClientStatusType>;
  }
};

export const getSubsidiaryUsersService = async (
  params?: ListSubsidiaryUserType
) => {
  const queryParams = params ? getQueryParams(params) : '';

  const response = await Fetch(
    `suppliers/subsidiaryuser/list-detail/${queryParams}`,
    {
      method: 'GET',
      signal: params?.signal,
    }
  );

  if (response.ok) {
    const data = await response.json();
    return data as ListAPIResponseType<SubsidiaryUserType>;
  }
};

export const createSubsidiaryClientStatusService = async (
  subsidiaryClientStatus: CreateSubsidiaryClientStatusType
) => {
  // Send request
  const response = await Fetch('suppliers/subsidiary_client_status/', {
    method: 'POST',
    body: JSON.stringify(subsidiaryClientStatus),
  });
  // Get response
  if (response.ok) {
    const data = await response.json();
    return data as SubsidiaryClientStatusType;
  }
  throw new Error('Error');
};

export const updateSubsidiaryClientStatusService = async (
  subsidiaryClientStatus: UpdateSubsidiaryClientStatusType
) => {
  // Send request
  const response = await Fetch(
    `suppliers/subsidiary_client_status/${subsidiaryClientStatus.id}/update/`,
    {
      method: 'PATCH',
      body: JSON.stringify(subsidiaryClientStatus),
    }
  );
  // Get response
  if (response.ok) {
    const data = await response.json();
    return data as SubsidiaryClientStatusType;
  }
  throw new Error('Error');
};

export const deleteSubsidiaryClientStatusService = async (
  subsidiaryClientStatus: DeleteSubsidiaryClientStatusType
) => {
  // Send request
  const response = await Fetch(
    `suppliers/subsidiary_client_status/${subsidiaryClientStatus.id}/delete`,
    {
      method: 'DELETE',
    }
  );
  // Get response
  if (response.ok) {
    return true;
  }
  throw new Error('Error');
};

export const getAssignmentGroupsService = async (
  params?: ListAssignmentGroupType
) => {
  const queryParams = params ? getQueryParams(params) : '';

  const response = await Fetch(`suppliers/assignment-groups/${queryParams}`, {
    method: 'GET',
    signal: params?.signal,
  });

  if (response.ok) {
    const data = await response.json();
    return data as ListAPIResponseType<AssignmentGroupType>;
  }
};

export const createAssignmentGroupService = async (
  assignmentGroup: CreateAssignmentGroupType
) => {
  // Send request
  const response = await Fetch('suppliers/assignment-groups/', {
    method: 'POST',
    body: JSON.stringify(assignmentGroup),
  });
  // Get response
  if (response.ok) {
    const data = await response.json();
    return data as AssignmentGroupType;
  }
  throw new Error('Error');
};

export const updateAssignmentGroupService = async (
  assignmentGroup: UpdateAssignmentGroupType
) => {
  // Send request
  const response = await Fetch(
    `suppliers/assignment-groups/${assignmentGroup.id}/update/`,
    {
      method: 'PATCH',
      body: JSON.stringify(assignmentGroup),
    }
  );
  // Get response
  if (response.ok) {
    const data = await response.json();
    return data as AssignmentGroupType;
  }
  throw new Error('Error');
};

export const deleteAssignmentGroupService = async (
  assignmentGroup: DeleteAssignmentGroupType
) => {
  // Send request
  const response = await Fetch(
    `suppliers/assignment-groups/${assignmentGroup.id}/delete/`,
    {
      method: 'DELETE',
    }
  );
  // Get response
  if (response.ok) {
    return true;
  }
  throw new Error('Error');
};

export const bulkReplaceSubsidiaryClientStatusService = async (params: {
  client_statuses: string[];
}) => {
  // Send request
  const response = await Fetch(
    `suppliers/subsidiary_client_status/bulk_replace/`,
    {
      method: 'PUT',
      body: JSON.stringify(params),
    }
  );
  // Get response
  if (response.ok) {
    const data = await response.json();
    return data as string[];
  }
  throw new Error('Error');
};

export const getDigitalMarketingChannelsService = async (params: {
  signal?: AbortSignal;
  is_active?: boolean;
  limit?: number;
  page?: number;
  search?: string;
  channel?: string;
}) => {
  const { signal, page, limit = 4, ...otherParams } = params;

  const offset = page ? (page - 1) * limit : 0;

  const queryParams = getQueryParams({
    ...otherParams,
    limit,
    offset,
  });

  const response = await Fetch(
    `suppliers/digital-marketing-channels/${queryParams}`,
    {
      method: 'GET',
      signal: params.signal,
    }
  );

  if (response.ok) {
    const data = await response.json();
    return data as ListAPIResponseType<DigitalMarketingChannelsType>;
  }
  throw new Error('Error fetching digital marketing channels');
};

export const createDigitalMarketingChannelService = async (
  params: CreateDigitalMarketing
) => {
  const response = await Fetch('suppliers/digital-marketing-channels/', {
    method: 'POST',
    body: JSON.stringify(params),
  });

  if (response.ok) {
    const data = await response.json();
    return data as DigitalMarketingChannelsType;
  }
  throw new Error('Error creating digital marketing channel');
};

export const deleteDigitalMarketingChannelService = async (id: number) => {
  const response = await Fetch(
    `suppliers/digital-marketing-channels/delete/${id}`,
    {
      method: 'DELETE',
    }
  );

  if (response.ok) {
    return true;
  }
  throw new Error('Error deleting digital marketing channel');
};

export const updateDigitalMarketingChannelService = async (
  params: UpdateDigitalMarketing
) => {
  const response = await Fetch(
    `suppliers/digital-marketing-channels/update/${params.id}`,
    {
      method: 'PATCH',
      body: JSON.stringify(params),
    }
  );

  if (response.ok) {
    const data = await response.json();
    return data as DigitalMarketingChannelsType;
  }
  throw new Error('Error deleting digital marketing channel');
};

export const digitalMarketingChannelAddClientService = async (params: {
  channel_id: number;
  client_id: string;
}) => {
  const response = await Fetch(
    `suppliers/digital-marketing-channels/${params.channel_id}/clients/${params.client_id}/add`,
    {
      method: 'PATCH',
    }
  );

  if (response.ok) {
    const data = await response.json();
    return data as DigitalMarketingChannelsType;
  }

  throw new Error('Error adding client to digital marketing channel');
};

export const getWorkScheduleOrderTakerDaysService = async (params?: {
  limit?: number;
  offset?: number;
}) => {
  const queryParams = params ? getQueryParams(params) : '';

  const response = await Fetch(`suppliers/work-schedules/${queryParams}`, {
    method: 'GET',
  });

  if (response.ok) {
    const data = await response.json();
    return data as ListAPIResponseType<WorkScheduleType>;
  }

  throw new Error('Error adding client to digital marketing channel');
};

export const createWorkScheduleOrderTakerDaysService = async (
  body: CreateWorkScheduleType
) => {
  const response = await Fetch('suppliers/work-schedules/', {
    method: 'POST',
    body: JSON.stringify(body),
  });

  if (response.ok) {
    const data = await response.json();
    return data as WorkScheduleType;
  }

  throw new Error('Error adding client to digital marketing channel');
};

export const updateWorkScheduleOrderTakerDaysService = async (
  params: WorkScheduleType
) => {
  const { id, ...body } = params;

  const response = await Fetch(`suppliers/work-schedules/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });

  if (response.ok) {
    const data = await response.json();
    return data as WorkScheduleType;
  }

  throw new Error('Error adding client to digital marketing channel');
};

export const updateSubsidiaryUserProportionsService = async (
  userProportions: Record<string, number>
) => {
  const response = await Fetch(
    'suppliers/subsidiaryuser/bulk-update-proportions/',
    {
      method: 'PATCH',
      body: JSON.stringify({ user_proportions: userProportions }),
    }
  );
  if (response.ok) {
    const data = await response.json();
    return data;
  }

  throw new Error('Error updating user proportions');
};

export const updateSubsidiaryUserCarBrandsService = async (
  userCarBrands: Record<string, number[]>
) => {
  const response = await Fetch(
    'suppliers/subsidiaryuser/bulk-update-car-brands/',
    {
      method: 'PATCH',
      body: JSON.stringify({ car_brands: userCarBrands }),
    }
  );

  if (response.ok) {
    const data = await response.json();
    return data;
  }

  throw new Error('Error updating user car brands');
};

export type CarFeedbackType = {
  feedback_type:
    'incorrect_pattern' | 'incorrect_car_vin' | 'incorrect_car_info';
  feedback_message: string;
  order_id: number;
  client_chat_url: string;
};

export const sendGoogleChatSpaceMessageService = async (
  body: CarFeedbackType
) => {
  const response = await Fetch('suppliers/google_chat/feedback', {
    method: 'POST',
    body: JSON.stringify(body),
  });

  if (response.ok) {
    const data = await response.json();
    return data;
  }

  throw new Error('Error updating user car brands');
};

export const getWhatsappReactivationConfigService = async (params: {
  signal?: AbortSignal;
}) => {
  const response = await Fetch('suppliers/whatsapp-reactivation-config/', {
    method: 'GET',
    signal: params.signal,
  });

  if (response.ok) {
    const data = await response.json();
    return data as ListAPIResponseType<WhatsappReactivationConfig>;
  }
};

export const updateWhatsappReactivationConfigService = async (
  params: Partial<UpdateWhatsappReactivationConfig>
) => {
  const response = await Fetch(
    'suppliers/whatsapp-reactivation-config/update/',
    {
      method: 'PATCH',
      body: JSON.stringify(params),
    }
  );

  if (response.ok) {
    const data = await response.json();
    return data as WhatsappReactivationConfig;
  }
  throw new Error('Error updating whatsapp reactivation config');
};

export const createWhatsappReactivationConfigService = async (
  params: WhatsappReactivationConfig
) => {
  const response = await Fetch('suppliers/whatsapp-reactivation-config/', {
    method: 'POST',
    body: JSON.stringify(params),
  });

  if (response.ok) {
    const data = await response.json();
    return data as WhatsappReactivationConfig;
  }
  throw new Error('Error creating whatsapp reactivation config');
};

export const getWelcomeMessageConfigService = async (params: {
  signal?: AbortSignal;
}) => {
  const response = await Fetch('suppliers/welcome-message-config/', {
    method: 'GET',
    signal: params.signal,
  });

  if (response.ok) {
    const data = await response.json();
    return data as ListAPIResponseType<WelcomeMessageConfig>;
  }
};

export const updateWelcomeMessageConfigService = async (
  params: Partial<UpdateWelcomeMessageConfig>
) => {
  const response = await Fetch('suppliers/welcome-message-config/update/', {
    method: 'PATCH',
    body: JSON.stringify(params),
  });

  if (response.ok) {
    const data = await response.json();
    return data as WelcomeMessageConfig;
  }
  throw new Error('Error updating welcome message config');
};

export const createWelcomeMessageConfigService = async (
  params: WelcomeMessageConfig
) => {
  const response = await Fetch('suppliers/welcome-message-config/', {
    method: 'POST',
    body: JSON.stringify(params),
  });

  if (response.ok) {
    const data = await response.json();
    return data as WelcomeMessageConfig;
  }
  throw new Error('Error creating welcome message config');
};
